import {
  ActionIcon,
  Box,
  Button,
  FileButton,
  Group,
  Image,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconPhotoPlus, IconUpload, IconX } from "@tabler/icons-react";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { repositoryPos, repositoryWeb } from "../../_base/_const/_constVar";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { MessageResponse } from "../../model/MessageResponse";

type PickedImage = {
  file: File;
  previewUrl: string;
};

type UploadedFile = {
  url?: string;
  objectName?: string;
};

/**
 * CDN lưu file theo đúng tên gửi lên trong thư mục của ngày, mà ảnh chụp hay
 * trùng tên (image.jpg, IMG_0001.jpg...). Đặt lại tên theo mã đơn để ảnh của
 * đơn này không đè lên ảnh của đơn khác.
 */
const buildFileName = (
  orderNumber: string,
  file: File,
  stamp: number,
  index: number,
) => {
  const extension = /\.([a-z0-9]+)$/i.exec(file.name)?.[1]?.toLowerCase();
  return `${orderNumber}_${stamp}_${index + 1}.${extension ?? "jpg"}`;
};

const OrderImageUploadModal = ({
  orderNumber,
  onUploaded,
}: {
  orderNumber: string;
  onUploaded?: () => void;
}) => {
  const [images, setImages] = useState<PickedImage[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const resetRef = useRef<() => void>(null);
  const imagesRef = useRef<PickedImage[]>([]);
  imagesRef.current = images;

  // Giải phóng ảnh xem trước khi đóng modal
  useEffect(
    () => () =>
      imagesRef.current.forEach((image) =>
        URL.revokeObjectURL(image.previewUrl),
      ),
    [],
  );

  const addFiles = (files: File[]) => {
    const picked = files
      .filter((file) => file.type.startsWith("image/"))
      .map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));

    if (picked.length < files.length) {
      NotificationExtension.Fails(
        "Chỉ đăng được file ảnh, các file khác đã được bỏ qua.",
      );
    }

    setImages((prev) => [...prev, ...picked]);
    // Cho phép chọn lại đúng file vừa bỏ ra
    resetRef.current?.();
  };

  const removeImage = (target: PickedImage) => {
    URL.revokeObjectURL(target.previewUrl);
    setImages((prev) => prev.filter((image) => image !== target));
  };

  /**
   * Tải tất cả ảnh lên CDN trong 1 lần, sau đó lưu từng link vào đơn
   * (create-video mỗi lần chỉ nhận 1 imageUrl). Ảnh nào lỗi thì giữ lại
   * trong danh sách để bấm đăng lại, ảnh đã lưu thì bỏ ra.
   */
  const handleUpload = async () => {
    if (images.length === 0) return;
    setIsUploading(true);

    try {
      const stamp = Date.now();
      const fileNames = images.map((image, index) =>
        buildFileName(orderNumber, image.file, stamp, index),
      );

      const formData = new FormData();
      images.forEach((image, index) =>
        formData.append("files", image.file, fileNames[index]),
      );

      const folder = `cdn/web/${moment().format("DDMMYYYY")}`;
      // Query của BE viết là forderFile, phải giữ đúng chính tả
      const uploadResponse = await repositoryWeb.post<
        MessageResponse<UploadedFile[]>
      >(
        `/api/v1/Upload/files?forderFile=${encodeURIComponent(folder)}`,
        formData,
      );

      // Lỗi HTTP đã được repository thông báo
      if (!uploadResponse) return;

      const uploaded = uploadResponse.success ? uploadResponse.data ?? [] : [];
      if (uploaded.length === 0) {
        NotificationExtension.Fails(
          uploadResponse.message || "Tải ảnh lên CDN thất bại !",
        );
        return;
      }

      const failed: PickedImage[] = [];
      for (const [index, image] of images.entries()) {
        const imageUrl =
          uploaded.find((item) => item?.url?.endsWith(`/${fileNames[index]}`))
            ?.url ?? uploaded[index]?.url;

        const saveResponse = imageUrl
          ? await repositoryPos.post<MessageResponse<any>>(
              "/api/v1/OrderVideo/create-video",
              { orderNumber, imageUrl },
            )
          : undefined;

        if (!saveResponse?.success) failed.push(image);
      }

      const savedCount = images.length - failed.length;

      // Có ảnh lưu được vào đơn thì tải lại danh sách để cập nhật trạng thái ảnh
      if (savedCount > 0) onUploaded?.();

      if (failed.length === 0) {
        NotificationExtension.Success(
          `Đã đăng ${savedCount} ảnh giao hàng cho đơn ${orderNumber}`,
        );
        modals.closeAll();
        return;
      }

      images
        .filter((image) => !failed.includes(image))
        .forEach((image) => URL.revokeObjectURL(image.previewUrl));
      setImages(failed);
      NotificationExtension.Fails(
        `Đã lưu ${savedCount}/${images.length} ảnh vào đơn. Còn ${failed.length} ảnh chưa lưu được, vui lòng bấm đăng lại.`,
      );
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Có lỗi khi đăng ảnh giao hàng !");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Stack gap="md" py="xs">
      <Text size="sm" c="dimmed">
        Đơn hàng <b>{orderNumber}</b>
        {images.length > 0 ? ` - đã chọn ${images.length} ảnh` : ""}
      </Text>

      <FileButton
        onChange={addFiles}
        accept="image/*"
        multiple
        resetRef={resetRef}
        disabled={isUploading}
      >
        {(props) => (
          <UnstyledButton
            {...props}
            w="100%"
            p="lg"
            disabled={isUploading}
            style={{
              border: "1px dashed var(--mantine-color-gray-4)",
              borderRadius: 8,
              background: "var(--mantine-color-gray-0)",
              opacity: isUploading ? 0.6 : 1,
            }}
          >
            <Stack align="center" gap={4}>
              <IconPhotoPlus
                size={32}
                stroke={1.5}
                color="var(--mantine-color-blue-6)"
              />
              <Text size="sm" fw={500}>
                Chọn ảnh giao hàng
              </Text>
              <Text size="xs" c="dimmed">
                Có thể chọn nhiều ảnh một lần
              </Text>
            </Stack>
          </UnstyledButton>
        )}
      </FileButton>

      {images.length > 0 && (
        <SimpleGrid cols={{ base: 3, sm: 4, md: 6 }} spacing="xs">
          {images.map((image) => (
            <Box
              key={image.previewUrl}
              pos="relative"
              style={{
                aspectRatio: "1 / 1",
                borderRadius: 6,
                overflow: "hidden",
                border: "1px solid var(--mantine-color-gray-3)",
              }}
            >
              <Image
                src={image.previewUrl}
                alt={image.file.name}
                fit="cover"
                w="100%"
                h="100%"
              />
              <Tooltip label="Bỏ ảnh này">
                <ActionIcon
                  pos="absolute"
                  top={4}
                  right={4}
                  size="sm"
                  radius="xl"
                  variant="white"
                  color="red"
                  aria-label={`Bỏ ảnh ${image.file.name}`}
                  disabled={isUploading}
                  onClick={() => removeImage(image)}
                >
                  <IconX size={14} stroke={2} />
                </ActionIcon>
              </Tooltip>
            </Box>
          ))}
        </SimpleGrid>
      )}

      <Group justify="flex-end" gap="xs">
        <Button
          variant="default"
          disabled={isUploading}
          onClick={() => modals.closeAll()}
        >
          Hủy
        </Button>
        <Button
          leftSection={<IconUpload size={16} />}
          loading={isUploading}
          disabled={images.length === 0}
          onClick={handleUpload}
        >
          {images.length > 0 ? `Đăng ${images.length} ảnh` : "Đăng ảnh"}
        </Button>
      </Group>
    </Stack>
  );
};

export default OrderImageUploadModal;
